import { Input, Modal } from 'antd';
import React, { FC, useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { colorConstants } from 'config/colorConstants';
import { sendNotification } from 'utils/helperMethods';
import { tradePromoOptimizationUrls } from 'config/urls';
import dayjs from 'dayjs';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { queryClient } from 'services/reactQueryService';
import isBetween from 'dayjs/plugin/isBetween';
import { TPODataContext } from 'contexts/TPOContext';
import { useNavigate } from 'react-router-dom';
import { UseMutationResult } from '@tanstack/react-query';
import { TPO_UI_LIMITS } from 'constants/tpo';

dayjs.extend(isBetween);
type Props = {
    open: boolean;
    setOpen: (open: boolean) => void;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    acquireEditRightsMutation: UseMutationResult<
        any,
        any,
        {
            planId: Number;
        },
        unknown
    >;
};

export const CustomModal = styled(Modal)`
    .ant-modal-content {
        border-radius: 8px !important;
    }
    .ant-modal-header {
        border-radius: 8px 8px 0px 0px !important;
        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 400 !important;
    }
`;

const TPODuplicatePlanModal: FC<Props> = ({ onCancel, open, setOpen, acquireEditRightsMutation }) => {
    const { selectedPlanInfo, showOptimizedPlanCalender, optimizedPlanId, isTpmTab } = useContext(TPODataContext);
    const navigate = useNavigate();
    //create project states
    const [planName, setPlanName] = useState('');

    useEffect(() => {
        if (open) {
            setPlanName(selectedPlanInfo?.planName + '_copy');
        }
    }, [open]);

    //create project api
    const { mutate: duplicatePlan, loading: duplicatePlanLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.duplicatePlan,
        method: 'post',
    });

    function resetAllFields() {
        setPlanName('');
    }
    //reset all fields to default when popup closes
    useEffect(() => {
        if (!open) {
            resetAllFields();

            //clear cache
            queryClient.removeQueries({
                exact: false,
                queryKey: ['ps-get-attribute-types'],
            });
        }
    }, [open]);

    //checking if any fields are missing
    //if any fields are missing, don't enable submit button
    function validateFields() {
        if (!planName?.trim() || planName?.length > TPO_UI_LIMITS.PLAN_NAME_MAX_LENGTH) return true;
        return false;
    }

    const getConfirmationMessage = () => {
        if (showOptimizedPlanCalender) {
            return `Are you sure you want to save a copy of the Optimized Plan ${selectedPlanInfo?.planName}?`;
        } else if (selectedPlanInfo?.isTpmPlan && isTpmTab) {
            return `Are you sure you want to save a copy of the TPM Plan ${selectedPlanInfo?.planName}?`;
        } else if (selectedPlanInfo?.isTpmPlan && !isTpmTab) {
            return `Are you sure you want to save a copy of the TPM Plan ${selectedPlanInfo?.planName}?`;
        } else {
            return `Are you sure you want to save a copy of the Plan ${selectedPlanInfo?.planName}?`;
        }
    };

    const onSubmit = () => {
        if (duplicatePlanLoading) return;

        const payload: any = {
            plan_id: showOptimizedPlanCalender ? optimizedPlanId : selectedPlanInfo?.planId,
            is_tpm_plan: showOptimizedPlanCalender ? false : selectedPlanInfo?.isTpmPlan, // setting false when its optimized plan
            is_tpm_tab: isTpmTab,
            created_by: localStorage.getItem('email'),
            plan_name: planName?.trim(),
        };

        duplicatePlan(
            { ...payload },
            {
                onSuccess: (data) => {
                    if (data?.data?.plan_id) {
                        sendNotification('success', 'Plan saved successfully');
                        navigate(`/promo/recommendation/tpo/plan/update/${data?.data?.plan_id}`);
                        acquireEditRightsMutation.mutateAsync({ planId: data?.data?.plan_id });
                        setOpen(false);
                    } else {
                        console.log('data ', data);
                        sendNotification('error', 'Failed to save plan copy');
                    }
                },
                onError: (error: any) => {
                    console.log('error ', error);
                    sendNotification('error', error?.response?.data?.detail || 'Failed to save plan copy');
                },
            },
        );
    };
    const isSubmitButtonDisabled = validateFields();

    return (
        <CustomModal
            bodyStyle={{ background: colorConstants.greyAlt9 }}
            style={{ borderRadius: 8 }}
            cancelButtonProps={{ style: { borderRadius: 6 } }}
            okButtonProps={{ style: { borderRadius: 6 }, disabled: isSubmitButtonDisabled }}
            confirmLoading={duplicatePlanLoading}
            okText={'Save'}
            maskClosable={false}
            destroyOnClose
            centered
            onOk={onSubmit}
            open={open}
            width={450}
            onCancel={(e) => {
                if (duplicatePlanLoading) return;
                onCancel(e);
            }}
            title={'Save As Plan'}
        >
            <div className="flex-col flex gap-4">
                <div className="flex items-center">{getConfirmationMessage()}</div>
                <div className="flex gap-2 flex-col">
                    <div className="flex items-center">
                        <span className="text-redAlt mr-1">* </span>Plan Name :
                    </div>
                    <Input
                        value={planName}
                        type="text"
                        onChange={(e) => setPlanName(e.target.value)}
                        style={{ fontSize: 13, width: '80%', borderRadius: 6 }}
                        placeholder="Input"
                    />
                    <div className="text-redAlt text-xs h-5 flex items-center">
                        {planName.length > TPO_UI_LIMITS.PLAN_NAME_MAX_LENGTH &&
                            `Plan name cannot exceed ${TPO_UI_LIMITS.PLAN_NAME_MAX_LENGTH} characters`}
                    </div>
                </div>
            </div>
        </CustomModal>
    );
};

export default TPODuplicatePlanModal;
